import { FetchResult } from '@apollo/client'
import { isActivityLog } from '@components/Comments/ActivityLog/logEntryUtils'
import {
  Comment,
  DeadlineRequest,
  GetResourcePolicyQuery,
  LogEntry,
  Resource,
} from '@lib/graphql/__generated__/graphql'
import { SYSTEM_MESSAGE_PREFIX } from '@lib/hooks/useCommentSystemMessage'
import { UUID } from '@lib/types/general'
import { Goal } from '@lib/types/goals'
import { Task } from '@lib/types/task'
import { User } from '@lib/types/user'
import {
  formatFileSize,
  uploadFileToStorage,
} from '@mono/packages/utils/attachments'
import { TFunction } from '@translations/useTranslations'
import { differenceInSeconds } from 'date-fns'
import filter from 'ramda/src/filter'
import isEmpty from 'ramda/src/isEmpty'
import map from 'ramda/src/map'
import pipe from 'ramda/src/pipe'
import uniqBy from 'ramda/src/uniqBy'
import { QUERY_ACTIONS } from './constants'
import { formatDate, getDateFromTimeStamp } from './Date'
import { formatDisplayedEmail, formatEmailAddresses } from './Email'
import { isDuplicate, isParent, isSubTask, isUserAssignee } from './Task'

export const serializeRecipientsForUpdate = (recipients) => ({
  users:
    recipients?.users?.map(({ user, sendEmail }) => ({
      userId: user?.id,
      sendEmail,
    })) ?? [],
  teams:
    recipients?.teams?.map(({ team, sendEmail }) => ({
      teamId: team?.id,
      sendEmail,
    })) ?? [],
})

export const isActiveCommentRecipientEntity = (
  entity: Partial<RelatedEntity>,
) => !!entity && (entity.__typename === 'Team' || entity.isActive !== false)

export const filterActiveCommentRecipientEntities = <
  T extends Partial<RelatedEntity>,
>(
  entities: T[],
): T[] =>
  entities?.filter((entity) => isActiveCommentRecipientEntity(entity)) ?? []

export const getAllRelatedUsers = ({
  user,
  currentTask,
  relatedTasks,
  initialUsers,
}: {
  user: User
  currentTask: Task
  relatedTasks: Task[]
  initialUsers?: User[]
}) => {
  const relatedUsers = relatedTasks
    .filter(
      (task) =>
        task.id === currentTask.id ||
        task.rootTaskId === currentTask.id ||
        task.parentTaskId === currentTask.id,
    )
    .reduce((all, task: Task) => {
      const followers = task?.followers ?? []
      const assignees = task?.assignees || []
      const owner = task.owner
      const shouldAddOwner = user.id !== task.owner.id
      const related = shouldAddOwner
        ? [...all, ...followers, ...assignees, owner]
        : [...all, ...followers, ...assignees]

      return related
    }, [])

  return pipe(
    filter((u) => u.id !== user?.id && isActiveCommentRecipientEntity(u)),
    uniqBy((i: User) => i.id),
  )([...relatedUsers, ...(initialUsers || [])])
}
export const getTaskDefaultUsersToBeNotified = ({
  account,
  currentTask,
  relatedTasks,
  parentComment,
}: {
  account: User
  currentTask: Task
  relatedTasks: Task[]
  parentComment: Comment
}) => {
  const { user: parentCommentOwner, recipients } = parentComment || {}

  function getAllRelatedAssignees(relatedTasks, condition) {
    return relatedTasks.reduce((all, task) => {
      if (condition(task)) {
        return [...all, ...task.assignees]
      }

      return all
    }, [])
  }

  let assignees = []
  let followers = []

  if (isParent(currentTask)) {
    assignees = currentTask.assignees.filter((a) => a.id !== account.id)
    followers = currentTask.followers
  } else if (isDuplicate(currentTask)) {
    followers = currentTask.followers
    assignees = isUserAssignee(account, currentTask)
      ? getAllRelatedAssignees(
          relatedTasks,
          (task) => task.parentTaskId === currentTask.id,
        )
      : currentTask.assignees
  } else if (isSubTask(currentTask)) {
    assignees = isUserAssignee(account, currentTask)
      ? []
      : currentTask.assignees
    followers = currentTask.followers
  }
  const parentUserRecipients =
    recipients?.users?.filter((r) => !r?.isTeamMember).map((r) => r.user) ?? []

  const parentTeamRecipients = recipients?.teams?.map((r) => r.team) ?? []

  const owner = currentTask.owner
  const shouldAddOwner = account.id !== currentTask.owner.id
  const baseUsers = [
    ...assignees,
    ...followers,
    ...parentUserRecipients,
    ...parentTeamRecipients,
    parentCommentOwner,
  ]

  const defaultUsers = (
    shouldAddOwner ? [...baseUsers, owner] : baseUsers
  ).filter(
    (u) => !!u && u.id !== account.id && isActiveCommentRecipientEntity(u),
  )

  return uniqBy((i: User) => i.id, defaultUsers)
}

export const getGoalDefaultUsersToBeNotified = ({
  account,
  goal,
  parentComment,
}: {
  account: User
  goal: Goal
  parentComment: Comment
}) => {
  const { user: parentCommentOwner, recipients } = parentComment || {}

  const users = [goal.owner] as any[]

  const parentUserRecipients =
    recipients?.users?.filter((r) => !r?.isTeamMember).map((r) => r.user) ?? []

  const parentTeamRecipients = recipients?.teams?.map((r) => r.team) ?? []

  if (parentCommentOwner) {
    users.push(parentCommentOwner)
  }

  if (goal.assignee) {
    users.push(goal.assignee)
  }

  if (Array.isArray(goal.followers)) {
    users.push(...goal.followers)
  }

  if (!isEmpty([...parentUserRecipients, ...parentTeamRecipients])) {
    users.push(...parentUserRecipients, ...parentTeamRecipients)
  }

  const filteredUsers = users.filter(
    (u) => u && u.id !== account?.id && isActiveCommentRecipientEntity(u),
  )

  return uniqBy((u) => u.id, filteredUsers)
}

export interface RelatedEntity {
  id: string
  __typename?: string
  isActive?: boolean | null
  isGuest?: boolean
  fullName?: string | null
  email?: string | null
  name?: string | null
}

type PartialEntity = Partial<RelatedEntity>

interface RelatedEntitySource {
  owner?: PartialEntity | null
  assignees?: PartialEntity[] | null
  assignee?: PartialEntity | null
  followers?: PartialEntity[]
  teamFollowers?: PartialEntity[]
  participants?: { users?: PartialEntity[]; teams?: PartialEntity[] } | null
  comments?:
    | {
        recipients?: {
          users?: { user?: PartialEntity | null }[]
          teams?: { team?: PartialEntity | null }[]
        } | null
      }[]
    | null
}

export const getEntityRelatedEntities = (
  entity: RelatedEntitySource | null | undefined,
  excludeUserId?: string,
): RelatedEntity[] => {
  if (!entity) return []

  const candidates: PartialEntity[] = []

  if (entity.owner) candidates.push(entity.owner)
  if (entity.assignees) candidates.push(...entity.assignees)
  if (entity.assignee) candidates.push(entity.assignee)
  if (entity.followers) candidates.push(...entity.followers)
  if (entity.teamFollowers) candidates.push(...entity.teamFollowers)
  if (entity.participants) {
    candidates.push(...(entity.participants.users ?? []))
    candidates.push(...(entity.participants.teams ?? []))
  }

  const comments = entity.comments ?? []
  comments.forEach((comment) => {
    comment.recipients?.users?.forEach((r) => {
      if (r?.user) candidates.push(r.user)
    })
    comment.recipients?.teams?.forEach((r) => {
      if (r?.team) candidates.push(r.team)
    })
  })

  const filtered = candidates.filter(
    (u): u is RelatedEntity =>
      !!u?.id &&
      (!excludeUserId || u.id !== excludeUserId) &&
      isActiveCommentRecipientEntity(u),
  )
  return uniqBy((u: RelatedEntity) => u.id, filtered)
}

const buildAttachmentElement = (file) => {
  const fileData = {
    fileName: file?.name,
    resourceId: file?.key,
    src: `{{ ${file.key.replace(/\/cache\//g, '/')} }}`,
  }

  if (file?.type?.includes('image')) {
    return `<img id="${fileData.resourceId}" src="${fileData.src}" alt="${fileData.fileName}"/>`
  }

  return `<div class="w-[240px] rounded-md bg-white p-2 border border-oxford-gray-200 mr-2">
    <div class="flex items-center">
        <div class="flex flex-col min-w-0"><a class="truncate" href="${
          fileData.src
        }" tabindex="-1" contenteditable="false">${fileData.fileName}</a><span
                class="text-oxford-gray-400 text-xs">${formatFileSize(
                  file.size,
                )}</span></div>
        <div class="flex-1 flex justify-end items-center px-2"><i
                class="far fa-arrow-down-to-line text-xl text-oxford-gray-400"></i></div>
    </div>
</div>`
}

export const replaceLexicalAttachmentElements = ({
  existingFiles,
  newFiles,
  text,
}: {
  existingFiles?: Resource[]
  newFiles: any[]
  text: string
}) => {
  let replacedText = text
  const matchedExistingFilesIds = []

  existingFiles
    .filter(({ isInline = false }) => isInline)
    .map((file) => {
      const regexImgElement = new RegExp(`<img.*?>`, 'gm')

      const regexSrc = new RegExp(
        `https:.*?${file.presignedUrl.slice(-20)}`,
        'gm',
      )

      replacedText = replacedText.replace(regexImgElement, (imageMatch) => {
        if (imageMatch.match(regexSrc)) {
          matchedExistingFilesIds.push(file.id)

          return imageMatch.replace(regexSrc, `{{ ${file.key} }}`)
        }

        return imageMatch
      })
    })

  newFiles.map((file) => {
    const regexAttachementElement = new RegExp(
      // prettier-ignore
      '<img(\\s*)id="' + file.id + '".*?>',
      'gm',
    )

    const newAttachmentElement = buildAttachmentElement(file)

    replacedText = replacedText.replace(
      regexAttachementElement,
      newAttachmentElement,
    )
  })

  return { replacedText, matchedExistingFilesIds }
}

const addTargetBlankToLinks = (text) => {
  const html = new DOMParser().parseFromString(text, 'text/html')

  html.querySelectorAll('a').forEach((link) => {
    link.setAttribute('target', '_blank')
  })

  return html.body.innerHTML
}

export const serializeInlineFiles = (files) =>
  uniqBy((f) => f.key)(files.map(({ id, type, ...rest }) => rest))

export const formatCommentText = (data: Comment, t?: TFunction) => {
  const {
    text,
    resources,
    deadlineRequest,
    inboundEmailEnvelope,
    outboundEmailEnvelope,
  } = data || {}

  function withEmailEnvelopeHeader(newText: string) {
    if (inboundEmailEnvelope || outboundEmailEnvelope) {
      return formatEmailEnvelopeComment(data, t, newText)
    }

    return newText
  }

  if (deadlineRequest) {
    return formatDeadlineCommentRequest(text, deadlineRequest, t)
  }

  let newText = addTargetBlankToLinks(text)

  const inlineResources = resources?.filter((r) => r.isInline) ?? []

  if (isEmpty(inlineResources)) return withEmailEnvelopeHeader(newText)

  inlineResources.map((resource) => {
    const regexKeyPattern = new RegExp('{{ ' + resource.key + ' }}', 'gm')

    newText = newText.replaceAll(regexKeyPattern, resource.presignedUrl)
  })

  return withEmailEnvelopeHeader(newText)
}

const formatDeadlineCommentRequest = (
  text: string,
  deadlineRequest: DeadlineRequest,
  t: TFunction,
) => {
  const { proposedDeadline, task } = deadlineRequest

  return `<strong>${t('comments.proposedNewDeadline')}:</strong>
  ${formatDate(proposedDeadline, true)}
  <br/><strong>${t('comments.task')}:</strong> ${task.name}<br/>
  ${text ? `<strong>${t('comments.notes')}:</strong> ${text}` : ''}`
}

const formatEmailEnvelopeComment = (
  comment: Comment,
  t: TFunction,
  formattedText: string,
) => {
  const { inboundEmailEnvelope, outboundEmailEnvelope } = comment

  let from: string
  let to: string
  let cc: string
  let bcc: string
  let subject: string

  if (outboundEmailEnvelope) {
    from = formatDisplayedEmail(
      outboundEmailEnvelope.from?.email,
      outboundEmailEnvelope.from?.name,
      true,
    )
    to = formatEmailAddresses(outboundEmailEnvelope.to, true)
    cc = formatEmailAddresses(outboundEmailEnvelope.cc, true)
    bcc = formatEmailAddresses(outboundEmailEnvelope.bcc, true)
    subject = outboundEmailEnvelope.subject
  } else {
    from = formatDisplayedEmail(
      inboundEmailEnvelope.from.email,
      inboundEmailEnvelope.from.name,
      true,
    )
    to = formatEmailAddresses(inboundEmailEnvelope.to, true)
    cc = formatEmailAddresses(inboundEmailEnvelope.cc, true)
    bcc = formatEmailAddresses(inboundEmailEnvelope.bcc, true)

    subject = inboundEmailEnvelope.subject
  }

  return `<strong class="mr-1">${t(
    'emailIntegration.from',
  )}:</strong> <span class="text-oxford-gray-400">${from}</span> <br/>
  <strong class="mr-1">${t(
    'emailIntegration.to',
  )}:</strong> <span class="text-oxford-gray-400">${to}</span> <br/>
  ${
    cc?.length
      ? `<strong class="mr-1">CC:</strong> <span class="text-oxford-gray-400">${cc}</span> <br/>`
      : ''
  }
  ${
    bcc?.length
      ? `<strong class="mr-1">BCC:</strong> <span class="text-oxford-gray-400">${bcc}</span> <br/>`
      : ''
  }
   ${
     subject?.length
       ? `<p class="pb-1"><strong class="mr-1">${t(
           'emailIntegration.subject',
         )}:</strong> <span class="text-oxford-gray-400">${subject}</span> <br/></p>`
       : ''
   }
    <br/>
   <div class="email-container">${formattedText}</div>`
}

export const handleInlineFilesUpload = async ({
  inlineFiles,
  getPostPolicy,
  inline = true,
}) => {
  const inlineFilesUploaded = inlineFiles.map(async (inlineFile) => {
    const { id, file } = inlineFile

    const extention =
      file?.name?.slice(file?.name?.lastIndexOf('.'), Infinity) ?? ''

    const postPolicyResponse: FetchResult<GetResourcePolicyQuery> =
      await getPostPolicy({
        variables: {
          input: {
            filename: `${id}${extention}`,
          },
        },
      })

    const postPolicy = postPolicyResponse?.data?.getResourcePolicy

    if (!postPolicy) {
      throw new Error(
        postPolicyResponse?.errors?.[0]?.message ||
          'Failed to get resource policy.',
      )
    }

    const { url, key, contentType } = postPolicy

    await uploadFileToStorage(
      {
        id: crypto.randomUUID(),
        path: file?.path,
        size: file?.size,
        type: file?.type,
        category: null,
        name: file?.name ?? '',
        alias: file?.alias ?? '',
        object: file,
      },
      {
        url,
        onProgress: () => {},
        onError: () => {},
        onFinish: () => {},
        createAbortHandler: () => {},
        contentType,
      },
    )

    return {
      id,
      name: file?.name ?? '',
      size: file.size,
      type: file.type,
      isInline: inline,
      key,
    }
  })

  const filesResponse = await Promise.allSettled(inlineFilesUploaded)

  return pipe(
    filter(({ status }) => status === 'fulfilled'),
    map(({ value }) => value),
  )(filesResponse)
}

export const pushLibraryPath = ({ router, commentId, imageId }) =>
  imageId &&
  router?.push(
    {
      pathname: router.pathname,
      query: {
        ...router.query,
        commentId,
        imageId,
        commentAction: QUERY_ACTIONS.VIEW_COMMENT_LIBRARY,
      },
    },
    null,
    { shallow: true },
  )

export const handleInlineResourceClick =
  ({
    resources,
    router,
    commentId,
  }: {
    resources: Resource[]
    router: any
    commentId: UUID
  }) =>
  (clickEvent) => {
    if (
      clickEvent?.target?.tagName === 'IMG' &&
      clickEvent?.target?.closest('.comment-item')
    ) {
      const selectedImageId = resources?.find(
        ({ presignedUrl }) => clickEvent?.target?.src === presignedUrl,
      )?.id

      if (!selectedImageId && clickEvent?.target?.src) {
        return window.open(clickEvent.target.src, '_blank')
      }

      pushLibraryPath({ router, commentId, imageId: selectedImageId })
    }
  }

const getRecipientIds = (recipients) => {
  const userRecipients = recipients?.users?.map(({ user }) => user?.id) ?? []
  const teamRecipients = recipients?.teams?.map(({ team }) => team?.id) ?? []

  return [...userRecipients, ...teamRecipients]
}

const allRecipientsMatch = (firstRecipientIds, secondRecipientIds) => {
  if (firstRecipientIds.length !== secondRecipientIds.length) return false

  return firstRecipientIds.every((recipient) =>
    secondRecipientIds.includes(recipient),
  )
}

export const shouldNotRenderTitle = (
  comment: Comment,
  prevComment: Comment | LogEntry,
  commentsEditableState?: Record<UUID, boolean>,
) => {
  if (
    !prevComment ||
    isActivityLog(prevComment) ||
    commentsEditableState?.[comment?.id]
  )
    return false

  const currentUserId = comment?.user?.id
  const previousUserId = (prevComment as Comment)?.user?.id

  if (currentUserId !== previousUserId) return false

  const currentCreatedAt = getDateFromTimeStamp(comment?.createdAt)
  const previousCreatedAt = getDateFromTimeStamp(
    (prevComment as Comment)?.createdAt,
  )

  const secondsDiff = differenceInSeconds(currentCreatedAt, previousCreatedAt)

  const currentRecipients = getRecipientIds(comment?.recipients ?? {})
  const previousRecipients = getRecipientIds(
    (prevComment as Comment)?.recipients ?? {},
  )

  return (
    allRecipientsMatch(currentRecipients, previousRecipients) &&
    secondsDiff <= 300
  )
}

export const isCommentConvertedFromThread = (
  comment: Comment,
  allComments: (Comment | LogEntry)[],
) => {
  const newerComments = allComments.filter(
    (c) => c.createdAt?.seconds > comment.createdAt?.seconds,
  )
  return newerComments.some(
    (c) =>
      c.__typename === 'Comment' && c.text?.includes(SYSTEM_MESSAGE_PREFIX),
  )
}
